import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Menu, X, MapPin, Calendar, Clock, Bus, CreditCard, Send, CheckCircle2 } from "lucide-react";
import { PREVIEW, RSVP } from "@/constants/testIds";
import { formatHumanDate } from "@/lib/wedding";
import { DIET_OPTIONS, GUEST_TYPES, API } from "@/lib/admin";

function useCountdown(targetISO) {
    const target = useMemo(() => {
        const d = new Date(targetISO);
        return isNaN(d.getTime()) ? null : d.getTime();
    }, [targetISO]);

    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);

    if (!target) return { d: 0, h: 0, m: 0, s: 0, done: true };
    const diff = Math.max(0, target - now);
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    return { d, h, m, s, done: diff === 0 };
}

const pad = (n) => String(n).padStart(2, "0");

function Place({ id, eyebrow, place, testId, onMap }) {
    const name = place?.name || "";
    const address = place?.address || "";
    const time = place?.time || "";
    const mapUrl = place?.map_url || "";

    if (!name && !address && !time) return null;

    return (
        <section data-sec={id} className="px-5 py-10 border-b border-[var(--tpl-line)] text-center space-y-4">
            <div className="eyebrow">{eyebrow}</div>
            {name && <h3 className="display text-2xl">{name}</h3>}
            
            {(time || address) && (
                <div className="flex flex-col items-center gap-2 text-sm text-[var(--tpl-ink-soft)] font-light">
                    {time && (
                        <div className="flex items-center gap-1.5">
                            <Clock size={14} className="opacity-75" />
                            <span>{time} h</span>
                        </div>
                    )}
                    {address && (
                        <div className="flex items-center gap-1.5 max-w-[260px] justify-center">
                            <MapPin size={14} className="opacity-60 flex-shrink-0" />
                            <span>{address}</span>
                        </div>
                    )}
                </div>
            )}

            {mapUrl && (
                <button
                    data-testid={testId}
                    onClick={() => onMap(mapUrl)}
                    className="btn-soft text-xs px-4 py-2 mt-2"
                >
                    Ver mapa de Google
                </button>
            )}
        </section>
    );
}

export default function WeddingPreview({ data, scope = "inline" }) {
    const [menuOpen, setMenuOpen] = useState(false);
    const { d, h, m, s } = useCountdown(data.wedding_date);
    const tplClass = data.template === "minimal-modern" ? "tpl-minimal" : "tpl-classic";

    // Estados para el formulario RSVP local — modelo per-guest.
    // `guests` es un array. Cada entrada tiene SU PROPIA dieta y alergias,
    // que se persisten como un documento Guest independiente en MongoDB.
    const emptyGuest = () => ({
        name: "",
        guest_type: "adult",
        diet_restrictions: [],
        allergies: "",
    });
    const [rsvpForm, setRsvpForm] = useState({
        attending: null, // 'yes' | 'no'
        needsBus: "no",
        notes: "",
        guests: [emptyGuest()],
    });
    const [rsvpSent, setRsvpSent] = useState(false);
    const [rsvpSubmitting, setRsvpSubmitting] = useState(false);
    const [rsvpError, setRsvpError] = useState(null);
    const [copied, setCopied] = useState(false);

    const updateGuest = (idx, patch) =>
        setRsvpForm((prev) => ({
            ...prev,
            guests: prev.guests.map((g, i) => (i === idx ? { ...g, ...patch } : g)),
        }));

    const toggleGuestDiet = (idx, dietId) =>
        setRsvpForm((prev) => ({
            ...prev,
            guests: prev.guests.map((g, i) => {
                if (i !== idx) return g;
                const has = g.diet_restrictions.includes(dietId);
                return {
                    ...g,
                    diet_restrictions: has
                        ? g.diet_restrictions.filter((d) => d !== dietId)
                        : [...g.diet_restrictions, dietId],
                };
            }),
        }));

    const setGuestCount = (n) => {
        const target = Math.max(1, Math.min(8, n));
        setRsvpForm((prev) => {
            const current = prev.guests.length;
            if (target === current) return prev;
            if (target > current) {
                return {
                    ...prev,
                    guests: [
                        ...prev.guests,
                        ...Array.from({ length: target - current }, emptyGuest),
                    ],
                };
            }
            return { ...prev, guests: prev.guests.slice(0, target) };
        });
    };

    const handleCopyIban = () => {
        if (!data.iban) return;
        navigator.clipboard.writeText(data.iban);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleRsvpSubmit = async (e) => {
        e.preventDefault();
        if (rsvpForm.attending === null) return;
        const primary = rsvpForm.guests[0];
        if (!primary?.name.trim()) {
            setRsvpError("Pon al menos tu nombre.");
            return;
        }

        if (scope !== "public") {
            alert(
                "Esto es la simulación del editor. En la web pública real, este botón guarda los datos en tu panel.",
            );
            return;
        }

        const slug = data.slug;
        if (!slug) {
            setRsvpError("No se ha podido enviar.");
            return;
        }

        const attending = rsvpForm.attending === "yes";
        const notes = [
            rsvpForm.needsBus && rsvpForm.needsBus !== "no"
                ? `Autobús: ${rsvpForm.needsBus.replace("_", " / ")}`
                : "",
            (rsvpForm.notes || "").trim(),
        ]
            .filter(Boolean)
            .join(" · ");

        // Si no asiste, mandamos sólo al titular sin dietas/alergias.
        const guestsPayload = attending
            ? rsvpForm.guests
                  .filter((g) => (g.name || "").trim())
                  .map((g) => ({
                      name: g.name.trim(),
                      guest_type: g.guest_type,
                      diet_restrictions: g.diet_restrictions,
                      allergies: (g.allergies || "").trim(),
                  }))
            : [
                  {
                      name: primary.name.trim(),
                      guest_type: primary.guest_type,
                      diet_restrictions: [],
                      allergies: "",
                  },
              ];

        setRsvpSubmitting(true);
        setRsvpError(null);
        try {
            await axios.post(
                `${API}/public/weddings/by-slug/${slug}/rsvp`,
                { guests: guestsPayload, attending, notes },
            );
            setRsvpSent(true);
        } catch (err) {
            console.error("RSVP submit failed", err);
            setRsvpError("No se ha podido guardar. Inténtalo de nuevo.");
        } finally {
            setRsvpSubmitting(false);
        }
    };

    const sections = [
        { id: "intro", label: "Inicio" },
        { id: "message", label: "Mensaje" },
        { id: "countdown", label: "Cuenta atrás" },
        { id: "ceremony", label: "Ceremonia" },
        { id: "party", label: "Celebración" },
        { id: "bus", label: "Autobús" },
        { id: "iban", label: "Regalo" },
        { id: "rsvp", label: "Confirmar" },
    ];

    const scrollTo = (id) => {
        setMenuOpen(false);
        const root = document.getElementById(`wp-scope-${scope}`);
        const el = root?.querySelector(`[data-sec="${id}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const handleMapClick = (url) => {
        if (!url) return;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    return (
        <div
            id={`wp-scope-${scope}`}
            data-testid={PREVIEW.root}
            className={`${tplClass} tpl-anim h-full w-full overflow-y-auto no-scrollbar relative`}
            key={data.template}
        >
            {/* Barra superior con hamburguesa */}
            <div className="sticky top-0 z-20 flex items-center justify-between px-5 py-3 backdrop-blur-md bg-[var(--tpl-bg)]/70 border-b border-[var(--tpl-line)]">
                <div className="font-serif text-base" style={{ letterSpacing: "0.08em" }}>
                    {(data.primary_name || "B").charAt(0)} & {(data.secondary_name || "G").charAt(0)}
                </div>
                <button
                    data-testid={PREVIEW.menuBtn}
                    onClick={() => setMenuOpen(true)}
                    aria-label="Abrir menú"
                    className="w-10 h-10 inline-flex items-center justify-center rounded-full border border-[var(--tpl-line)] bg-[var(--tpl-bg)]/60"
                >
                    <Menu size={18} />
                </button>
            </div>

            {/* Menú Lateral */}
            {menuOpen && (
                <div className="menu-sheet" data-testid={PREVIEW.menuSheet}>
                    <button
                        data-testid={PREVIEW.menuClose}
                        onClick={() => setMenuOpen(false)}
                        aria-label="Cerrar menú"
                        className="absolute top-4 right-4 w-10 h-10 inline-flex items-center justify-center rounded-full border border-[var(--tpl-line)]"
                    >
                        <X size={18} />
                    </button>
                    <div className="eyebrow mb-4">Menú</div>
                    <div className="flex flex-col gap-4">
                        {sections.map((sec) => {
                            if (sec.id === "bus" && !data.busEnabled) return null;
                            if (sec.id === "iban" && !(data.ibanEnabled && data.iban)) return null;
                            if (sec.id === "countdown" && !(data.countdownEnabled ?? true)) return null;
                            if (sec.id === "message" && !((data.messageEnabled ?? true) && data.message)) return null;
                            return (
                                <button
                                    key={sec.id}
                                    data-testid={`menu-link-${sec.id}`}
                                    onClick={() => scrollTo(sec.id)}
                                    className="text-left display text-3xl"
                                >
                                    {sec.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Portada Principal */}
            <section data-sec="intro" className="relative">
                <div
                    className="h-[58vh] min-h-[340px] w-full bg-cover bg-center relative"
                    style={{
                        backgroundImage: data.cover_image ? `url(${data.cover_image})` : undefined,
                        background: data.cover_image
                            ? `linear-gradient(180deg, rgba(0,0,0,0.0) 0%, rgba(0,0,0,0.35) 100%), url(${data.cover_image}) center/cover`
                            : "linear-gradient(180deg, var(--tpl-bg), var(--tpl-line))",
                    }}
                >
                    <div className="absolute inset-0 flex items-end p-6">
                        <div className="text-white">
                            <div className="eyebrow mb-2" style={{ color: "rgba(255,255,255,0.9)" }}>
                                {data.tagline || "Nos casamos"}
                            </div>
                            <h1 className="display" style={{ fontSize: "clamp(40px, 11vw, 64px)", lineHeight: 1.02 }}>
                                {data.primary_name || "—"}
                                <span className="opacity-70"> & </span>
                                <br />
                                {data.secondary_name || "—"}
                            </h1>
                            <div className="mt-3 inline-flex items-center gap-2 text-sm bg-white/15 backdrop-blur px-3 py-1.5 rounded-full">
                                <Calendar size={14} />
                                {formatHumanDate(data.wedding_date)}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Mensaje personal (justo después de la portada) */}
            {(data.messageEnabled ?? true) && data.message && (
                <section
                    data-sec="message"
                    className="px-5 py-12 text-center bg-[var(--tpl-line)]/20"
                >
                    <p className="font-serif italic text-lg leading-relaxed text-[var(--tpl-ink)] max-w-xs mx-auto">
                        &ldquo;{data.message}&rdquo;
                    </p>
                </section>
            )}

            {/* Cuenta Atrás */}
            {(data.countdownEnabled ?? true) && (
                <section data-sec="countdown" data-testid={PREVIEW.countdown} className="px-5 py-8 border-b border-[var(--tpl-line)]">
                    <div className="eyebrow text-center mb-4">Cuenta atrás</div>
                    <div className="countdown-row">
                        <div className="countdown-cell">
                            <div className="countdown-num" data-testid={PREVIEW.countdownDays}>{pad(d)}</div>
                            <div className="countdown-label">Días</div>
                        </div>
                        <div className="countdown-cell">
                            <div className="countdown-num" data-testid={PREVIEW.countdownHours}>{pad(h)}</div>
                            <div className="countdown-label">Horas</div>
                        </div>
                        <div className="countdown-cell">
                            <div className="countdown-num" data-testid={PREVIEW.countdownMinutes}>{pad(m)}</div>
                            <div className="countdown-label">Min</div>
                        </div>
                        <div className="countdown-cell">
                            <div className="countdown-num" data-testid={PREVIEW.countdownSeconds}>{pad(s)}</div>
                            <div className="countdown-label">Seg</div>
                        </div>
                    </div>
                </section>
            )}

            {/* Ubicaciones */}
            <Place id="ceremony" eyebrow="Ceremonia" place={data.ceremony} testId={PREVIEW.ceremonyMapBtn} onMap={handleMapClick} />
            <Place id="party" eyebrow="Celebración" place={data.party} testId={PREVIEW.partyMapBtn} onMap={handleMapClick} />

            {/* Autobús */}
            {data.busEnabled && (data.busDeparture || data.busReturn) && (
                <section data-sec="bus" className="px-5 py-10 border-b border-[var(--tpl-line)] text-center">
                    <Bus size={20} className="mx-auto mb-3 opacity-70" />
                    <div className="eyebrow mb-3">Servicio de Autobús</div>
                    {data.busDeparture && (
                        <div className="mb-4">
                            <p className="text-xs uppercase tracking-wider text-[var(--tpl-ink-soft)] opacity-60 mb-1">Ida / Salida</p>
                            <p className="display text-xl">{data.busDeparture}</p>
                        </div>
                    )}
                    {data.busReturn && (
                        <div>
                            <p className="text-xs uppercase tracking-wider text-[var(--tpl-ink-soft)] opacity-60 mb-1">Regreso / Vuelta</p>
                            <p className="display text-xl">{data.busReturn}</p>
                        </div>
                    )}
                </section>
            )}

            {/* Cuenta Regalo / IBAN */}
            {data.ibanEnabled && data.iban && (
                <section data-sec="iban" className="px-5 py-10 border-b border-[var(--tpl-line)] text-center space-y-4">
                    <CreditCard size={20} className="mx-auto opacity-70" />
                    <div className="eyebrow">Regalo de bodas</div>
                    <p className="text-sm text-[var(--tpl-ink-soft)] max-w-xs mx-auto">
                        Si deseas hacernos un detalle, te dejamos aquí nuestro número de cuenta. ¡Muchísimas gracias!
                    </p>
                    <button
                        onClick={handleCopyIban}
                        className="w-full max-w-xs mx-auto flex items-center justify-between p-3 border border-[var(--tpl-line)] rounded-xl bg-[var(--tpl-bg)] text-sm font-mono tracking-wider text-[var(--tpl-ink)] active:scale-[0.98] transition-transform"
                    >
                        <span className="truncate mr-2">{data.iban}</span>
                        <span className="flex-shrink-0 text-xs text-[var(--tpl-ink-soft)] flex items-center gap-1 font-sans font-medium">
                            {copied ? <><CheckCircle2 size={12} className="text-emerald-600" /> Copiado</> : "Copiar"}
                        </span>
                    </button>
                </section>
            )}

            {/* Formulario RSVP Interactivo */}
            <section data-sec="rsvp" data-testid={RSVP.sheet} className="px-5 py-10 border-b border-[var(--tpl-line)] max-w-md mx-auto">
                <div className="text-center mb-6">
                    <div className="eyebrow mb-1">Confirmación</div>
                    <h2 className="display text-2xl">¿Nos acompañas?</h2>
                </div>

                {rsvpSent ? (
                    <div data-testid={RSVP.success} className="text-center p-6 border border-[var(--tpl-line)] rounded-2xl space-y-2 bg-[var(--tpl-bg)]/40">
                        <CheckCircle2 size={32} className="mx-auto text-emerald-600" />
                        <p className="font-medium">¡Asistencia enviada!</p>
                        <p className="text-xs text-[var(--tpl-ink-soft)]">Muchas gracias por confirmar tu asistencia.</p>
                    </div>
                ) : (
                    <form onSubmit={handleRsvpSubmit} className="space-y-4">
                        <div>
                            <label className="block text-xs uppercase tracking-wider text-[var(--tpl-ink-soft)] mb-2 font-medium">¿Asistirás a la boda?</label>
                            <div className="grid grid-cols-2 gap-2">
                                <button
                                    type="button"
                                    data-testid="rsvp-attending-yes"
                                    className={`py-2.5 text-xs font-medium rounded-xl border transition-colors ${rsvpForm.attending === "yes" ? "bg-[var(--tpl-ink)] text-[var(--tpl-bg)] border-transparent" : "bg-[var(--tpl-bg)] border-[var(--tpl-line)]"}`}
                                    onClick={() => setRsvpForm({ ...rsvpForm, attending: "yes" })}
                                >
                                    Sí, ¡allí estaré!
                                </button>
                                <button
                                    type="button"
                                    data-testid="rsvp-attending-no"
                                    className={`py-2.5 text-xs font-medium rounded-xl border transition-colors ${rsvpForm.attending === "no" ? "bg-[var(--tpl-ink)] text-[var(--tpl-bg)] border-transparent" : "bg-[var(--tpl-bg)] border-[var(--tpl-line)]"}`}
                                    onClick={() => setRsvpForm({ ...rsvpForm, attending: "no" })}
                                >
                                    No podré ir
                                </button>
                            </div>
                        </div>

                        {rsvpForm.attending === "yes" && (
                            <>
                                {/* Contador de asistentes */}
                                <div>
                                    <label className="block text-xs uppercase tracking-wider text-[var(--tpl-ink-soft)] mb-2 font-medium">¿Cuántos venís en total?</label>
                                    <div data-testid="rsvp-guest-count" className="flex items-center justify-between bg-[var(--tpl-bg)] border border-[var(--tpl-line)] rounded-xl px-2 py-1.5">
                                        <button
                                            type="button"
                                            data-testid="rsvp-guest-count-minus"
                                            onClick={() => setGuestCount(rsvpForm.guests.length - 1)}
                                            disabled={rsvpForm.guests.length <= 1}
                                            className="w-10 h-10 rounded-lg text-lg font-medium disabled:opacity-30"
                                            aria-label="Quitar asistente"
                                        >−</button>
                                        <span className="text-sm font-medium">
                                            {rsvpForm.guests.length} {rsvpForm.guests.length === 1 ? "asistente" : "asistentes"}
                                        </span>
                                        <button
                                            type="button"
                                            data-testid="rsvp-guest-count-plus"
                                            onClick={() => setGuestCount(rsvpForm.guests.length + 1)}
                                            disabled={rsvpForm.guests.length >= 8}
                                            className="w-10 h-10 rounded-lg text-lg font-medium disabled:opacity-30"
                                            aria-label="Añadir asistente"
                                        >+</button>
                                    </div>
                                    <p className="text-[11px] text-[var(--tpl-ink-soft)] mt-1.5 leading-snug">
                                        Cada asistente tendrá su propio bloque de alergias y dieta — así el catering lo tiene claro.
                                    </p>
                                </div>

                                {/* Bloques per-guest */}
                                {rsvpForm.guests.map((g, idx) => (
                                    <div
                                        key={idx}
                                        data-testid={`rsvp-guest-block-${idx}`}
                                        className="border border-[var(--tpl-line)] rounded-2xl p-4 space-y-3 bg-[var(--tpl-bg)]/30"
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--tpl-ink-soft)]">
                                                {idx === 0 ? "Tú" : `Invitado/a ${idx + 1}`}
                                            </div>
                                            {idx > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={() => setGuestCount(rsvpForm.guests.length - 1)}
                                                    className="text-[11px] text-[var(--tpl-ink-soft)] opacity-70 hover:opacity-100"
                                                    aria-label={`Quitar invitado ${idx + 1}`}
                                                >
                                                    Quitar
                                                </button>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-xs uppercase tracking-wider text-[var(--tpl-ink-soft)] mb-1 font-medium">Nombre</label>
                                            <input
                                                data-testid={idx === 0 ? RSVP.fieldName : `rsvp-guest-${idx}-name`}
                                                type="text"
                                                placeholder={idx === 0 ? "Ej. Carlos Martínez" : "Nombre del acompañante"}
                                                className="w-full p-3 text-sm bg-[var(--tpl-bg)] border border-[var(--tpl-line)] rounded-xl focus:outline-none"
                                                value={g.name}
                                                onChange={(e) => updateGuest(idx, { name: e.target.value })}
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-xs uppercase tracking-wider text-[var(--tpl-ink-soft)] mb-1.5 font-medium">Tipo</label>
                                            <div
                                                data-testid={idx === 0 ? RSVP.fieldType : `rsvp-guest-${idx}-type`}
                                                className="grid grid-cols-3 gap-2"
                                            >
                                                {GUEST_TYPES.map((t) => (
                                                    <button
                                                        type="button"
                                                        key={t.id}
                                                        onClick={() => updateGuest(idx, { guest_type: t.id })}
                                                        className={`py-2 text-xs font-medium rounded-xl border transition-colors ${g.guest_type === t.id ? "bg-[var(--tpl-ink)] text-[var(--tpl-bg)] border-transparent" : "bg-[var(--tpl-bg)] border-[var(--tpl-line)]"}`}
                                                    >
                                                        {t.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-xs uppercase tracking-wider text-[var(--tpl-ink-soft)] mb-1.5 font-medium">Dieta (opcional)</label>
                                            <div className="flex flex-wrap gap-1.5">
                                                {DIET_OPTIONS.map((opt) => {
                                                    const on = g.diet_restrictions.includes(opt.id);
                                                    const tid = idx === 0
                                                        ? RSVP.dietChip(opt.id)
                                                        : `rsvp-guest-${idx}-diet-${opt.id}`;
                                                    return (
                                                        <button
                                                            type="button"
                                                            key={opt.id}
                                                            data-testid={tid}
                                                            onClick={() => toggleGuestDiet(idx, opt.id)}
                                                            className={`px-3 py-1.5 rounded-full border text-xs transition-colors ${on ? "bg-[var(--tpl-ink)] text-[var(--tpl-bg)] border-transparent" : "bg-[var(--tpl-bg)] border-[var(--tpl-line)]"}`}
                                                        >
                                                            {opt.label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-xs uppercase tracking-wider text-[var(--tpl-ink-soft)] mb-1 font-medium">Información adicional (opcional)</label>
                                            <p className="text-[11px] text-[var(--tpl-ink-soft)] opacity-80 mb-1.5 leading-snug">
                                                Ej. necesidades de movilidad, trona para bebé, otros comentarios.
                                            </p>
                                            <input
                                                data-testid={idx === 0 ? RSVP.fieldAllergies : `rsvp-guest-${idx}-allergies`}
                                                type="text"
                                                placeholder="Cualquier otra cosa que debamos saber..."
                                                className="w-full p-3 text-sm bg-[var(--tpl-bg)] border border-[var(--tpl-line)] rounded-xl focus:outline-none"
                                                value={g.allergies}
                                                onChange={(e) => updateGuest(idx, { allergies: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                ))}

                                {data.busEnabled && (
                                    <div>
                                        <label className="block text-xs uppercase tracking-wider text-[var(--tpl-ink-soft)] mb-1 font-medium">¿Necesitáis plaza de autobús? (Para todos)</label>
                                        <select
                                            className="w-full p-3 text-sm bg-[var(--tpl-bg)] border border-[var(--tpl-line)] rounded-xl focus:outline-none appearance-none"
                                            value={rsvpForm.needsBus}
                                            onChange={(e) => setRsvpForm({ ...rsvpForm, needsBus: e.target.value })}
                                        >
                                            <option value="no">No necesitamos autobús</option>
                                            <option value="ida">Solo viaje de Ida</option>
                                            <option value="vuelta">Solo viaje de Vuelta</option>
                                            <option value="ida_vuelta">Necesitamos Ida y Vuelta</option>
                                        </select>
                                    </div>
                                )}
                            </>
                        )}

                        {rsvpError && (
                            <p className="text-xs text-red-600">{rsvpError}</p>
                        )}

                        <button
                            data-testid={RSVP.submit}
                            type="submit"
                            disabled={rsvpSubmitting}
                            className="w-full py-3 bg-[var(--tpl-ink)] text-[var(--tpl-bg)] font-medium rounded-xl text-xs uppercase tracking-widest hover:opacity-90 active:scale-[0.99] transition-all flex items-center justify-center gap-2 disabled:opacity-60"
                        >
                            <Send size={12} />
                            {rsvpSubmitting ? "Enviando..." : "Enviar confirmación"}
                        </button>
                        
                        {/* WhatsApp sutil abajo */}
                        <div className="text-center pt-4">
                            <p className="text-xs text-[var(--tpl-ink-soft)] opacity-80">
                                ¿Tienes alguna duda o prefieres avisar directamente?{" "}
                                {data.phone ? (
                                    <a
                                        href={`https://wa.me/${data.phone.replace(/[^0-9]/g, "")}?text=${encodeURIComponent("¡Hola! Tengo una duda sobre la invitación de boda...")}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="underline font-medium text-[var(--tpl-ink)] hover:opacity-80 transition-opacity"
                                    >
                                        Contactar por WhatsApp
                                    </a>
                                ) : (
                                    <span className="underline font-medium text-[var(--tpl-ink)] opacity-60">
                                        Contactar por WhatsApp
                                    </span>
                                )}
                            </p>
                        </div>
                    </form>
                )}
            </section>

            {/* Mensaje personal se muestra arriba, justo después de la portada. */}
        </div>
    );
}